import useApp from '@/hooks/use-app.hook';
import { Box, Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import { Outlet, useLocation } from '@tanstack/react-router';
import { ASSUME_TOAST_HEIGHT } from '../toasts/assume-role/inline';
import useIntercom from '@/hooks/use-intercom.hook';
import { FC } from 'react';

interface Props {
  hasAssumeBanner: boolean;
}

const addedBannerHeight = (hasAssumeBanner: boolean, bannerSize: number) =>
  (hasAssumeBanner ? ASSUME_TOAST_HEIGHT : 0) + bannerSize + 64;

const useStyles = createUseStyles<
  string,
  { signedIn: boolean; hasAssumeBanner: boolean; bannerSize: number }
>({
  containerScroll: ({ hasAssumeBanner, bannerSize }) => ({
    width: '100%',
    // account for app-header height, and assume-role/intercom banner height. If you changes those, you MUST change this
    height:
      hasAssumeBanner || bannerSize
        ? `calc(100% - ${addedBannerHeight(hasAssumeBanner, bannerSize)}px)`
        : 'calc(100% - 64px)',
    overflowY: 'auto',
    paddingTop: 24,
  }),
  container: ({ signedIn }) => ({
    width: '100%',
    minHeight: signedIn ? '100%' : 'inherit',
    height: signedIn ? '100%' : 'inherit',
    boxSizing: 'border-box',
    margin: '0 auto',
  }),
});

const AppContent: FC<Props> = ({ hasAssumeBanner }) => {
  const { signedIn, pageTitle } = useApp();
  const { pathname } = useLocation();
  const { bannerSize } = useIntercom();

  const { plain } = pageTitle;
  const hasPageTitle = Boolean(plain);

  const styles = useStyles({ signedIn, hasAssumeBanner, bannerSize });
  return (
    <Box className={styles.containerScroll}>
      <Box className={styles.container} maxWidth="xl" px={2}>
        {!signedIn && hasPageTitle && (
          <Stack alignItems="center">
            <Text size="x-large" noWrap>
              {plain}
            </Text>
          </Stack>
        )}
        <Outlet key={pathname} />
      </Box>
    </Box>
  );
};

export default AppContent;
